import {
  Banner,
  Button,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@scripsteam/scrips-signal-ds';
import type { BulkEnrolResponse, BulkOutcome } from '@/types/cohort-api';
import type { EnrolPatient } from './enrol-in-cohort-dialog';

/**
 * Per-patient result of a bulk enrolment (PROD-3206). Every patient gets its own row, so the run
 * never collapses to one "done" or one error. The headline is "N of M enrolled".
 * Already enrolled is not an error. Retry reopens the form for the refused (Invalid) rows only;
 * a NotFound patient cannot succeed on a retry, so it is never offered.
 */

const OUTCOME: Record<
  BulkOutcome,
  { label: string; semantic: 'success' | 'info' | 'warning' | 'error' }
> = {
  Created: { label: 'Enrolled', semantic: 'success' },
  AlreadyMember: { label: 'Already enrolled', semantic: 'info' },
  NotFound: { label: 'Not found', semantic: 'warning' },
  Invalid: { label: 'Refused', semantic: 'error' },
};

export interface BulkEnrolResultsProps {
  result: BulkEnrolResponse;
  patients: EnrolPatient[];
  onRetry: () => void;
}

export function BulkEnrolResults({ result, patients, onRetry }: BulkEnrolResultsProps) {
  const byId = new Map(patients.map((p) => [p.id, p]));
  const refused = result.results.filter((r) => r.outcome === 'Invalid').length;
  const notFound = result.results.filter((r) => r.outcome === 'NotFound').length;
  return (
    <div data-slot="enrol-results" className="flex flex-col gap-3">
      <div>
        <p data-slot="enrol-big" className="text-size-md font-semibold text-text-primary">
          {result.created} of {result.total} enrolled
        </p>
        <p data-slot="enrol-quiet" className="text-size-sm text-text-muted">
          {result.alreadyMember} already enrolled · {result.failed} not enrolled
        </p>
      </div>

      {result.failed > 0 ? (
        <Banner
          variant="warning"
          title={`${result.failed} not enrolled`}
          action={
            refused > 0 ? (
              <Button type="button" size="sm" variant="secondary" onClick={onRetry}>
                {`Retry ${refused} refused`}
              </Button>
            ) : undefined
          }
        >
          {refused > 0
            ? 'Check the reason on each row. Retry lets you change the dates or notes for the refused patients first.'
            : 'Check the reason on each row.'}
          {notFound > 0 ? ' Patients not found cannot be retried.' : ''}
        </Banner>
      ) : null}

      <div data-slot="enrol-table">
      <Table aria-label="Enrolment results">
        <TableHeader>
          <TableRow>
            <TableHead>Patient</TableHead>
            <TableHead>MRN</TableHead>
            <TableHead>Result</TableHead>
            <TableHead>Reason</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {result.results.map((row) => {
            const patient = byId.get(row.patientId);
            const outcome = OUTCOME[row.outcome];
            return (
              <TableRow key={row.patientId} data-outcome={row.outcome}>
                <TableCell>{patient?.name || row.patientId}</TableCell>
                <TableCell>{patient?.mrn ?? '—'}</TableCell>
                <TableCell>
                  <StatusChip semantic={outcome.semantic} tone="soft">
                    {outcome.label}
                  </StatusChip>
                </TableCell>
                <TableCell>
                  {row.error ?? (row.outcome === 'NotFound' ? 'No patient with this ID.' : '')}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
